import {
  resolveDisplay,
  type Card,
  type CardFormat,
  type ChartCard,
  type Timeframe,
} from '@/features/dashboard/api/schemas'
import type { ValueOptions } from './format'
import {
  changeFraction,
  chartTotals,
  formatValue,
  type ChangeUnit,
} from './format'

export type Figure = {
  value: number
  /** The previous period's value, which the figure rolls from. */
  previous: number | null
  /**
   * The change against `previous`: a fraction when `changeUnit` is relative,
   * else the plain difference in percentage points / points.
   */
  change: number | null
  changeUnit: ChangeUnit
  /** A fall is good news: the change's colours swap. */
  lowerIsBetter: boolean
  /** Decimals the abbreviated headline keeps; the kind's own when unset. */
  valueDecimals?: number | null | undefined
  unit?: string | null | undefined
  /** What the change compares, e.g. 'YoY' or 'YTD YoY'; none when not known. */
  label?: string | null | undefined
  format: CardFormat
  kind: Exclude<Card['kind'], 'empty'>
}

const lastTwo = (data: (number | null)[]): [number | null, number | null] => {
  const values = data.filter((v): v is number => v !== null)
  return [values.at(-1) ?? null, values.at(-2) ?? null]
}

export type Change = { amount: number; unit: ChangeUnit }

/**
 * The change per the card's unit; percent cards hold fractions, so a ppt is x100.
 * A relative change against a negative base it is
 * the plain difference in the card's own unit (-€0.02M to €31.75M is +€31.77M,
 * not +167753%), and against a zero base, or from a positive one to a negative
 * value, there is none to show.
 */
export const changeOf = (
  value: number,
  previous: number | null | undefined,
  format: CardFormat,
  unit: ChangeUnit
): Change | null => {
  if (previous == null) return null
  if (unit !== 'relative') {
    return {
      amount: (value - previous) * (format.style === 'percent' ? 100 : 1),
      unit,
    }
  }
  if (previous < 0) return { amount: value - previous, unit: 'abs' }
  if (previous === 0 || value < 0) return null
  const fraction = changeFraction(value, previous)
  return fraction === null ? null : { amount: fraction, unit }
}

export type FigureOptions = {
  /**
   * The expanded card's headline: the year-to-date figure the card reports
   * (see `reported`) in place of the tile's outlook.
   */
  expanded?: boolean
  /** What the change compares by default: the timeframe's, e.g. 'MoM'. */
  comparison?: string | null | undefined
}

/**
 * Builds the figures of a card: the shared parts (unit, format, decimals) once,
 * then a figure from a value, the one it follows and what the change compares.
 */
const figureBuilder = (card: Exclude<Card, { kind: 'empty' }>) => {
  const { unit, format, kind } = card
  const display = resolveDisplay(card)
  const shared = {
    unit,
    format,
    kind,
    lowerIsBetter: display.lowerIsBetter,
    // The headline is rounded as on the tile (€557M), not to the cent.
    valueDecimals: display.valueDecimals,
  }
  return (
    value: number,
    previous: number | null,
    label: string | null | undefined
  ): Figure => {
    const change = changeOf(value, previous, format, display.changeUnit)
    return {
      value,
      previous,
      change: change?.amount ?? null,
      changeUnit: change?.unit ?? display.changeUnit,
      label,
      ...shared,
    }
  }
}

/** A card's headline: its value, or a chart's latest total. */
export const toFigure = (
  card: Card,
  { expanded = false, comparison }: FigureOptions = {}
): Figure | null => {
  if (card.kind === 'empty') return null
  const figure = figureBuilder(card)
  switch (card.kind) {
    case 'kpi':
      return figure(card.value, card.previousValue ?? null, comparison)
    case 'chart': {
      const reported = expanded ? card.reported : undefined
      if (reported && reported.value != null) {
        return figure(
          reported.value,
          reported.previousValue ?? null,
          reported.comparison ?? card.comparison ?? comparison
        )
      }
      // Older payloads have no headline: use the last totals, then series[0].
      const [last, previous] =
        card.value !== undefined && card.value !== null
          ? [card.value, card.previousValue ?? null]
          : lastTwo(chartTotals(card))
      return last === null
        ? null
        : figure(last, previous, card.comparison ?? comparison)
    }
  }
}

/**
 * The expanded headline of a chart read at one of its bars: that bar's total
 * against the bar before it. Null where the bar has no value.
 */
export const toFigureAt = (
  card: ChartCard,
  index: number,
  label: string
): Figure | null => {
  const totals = chartTotals(card)
  const value = totals[index]
  if (typeof value !== 'number') return null
  const before = totals[index - 1]
  return figureBuilder(card)(
    value,
    typeof before === 'number' ? before : null,
    label
  )
}

/** What the change of a bar read on its own compares: the bar before it. */
export const previousPeriodLabel = (timeframe: Timeframe | undefined) => {
  switch (timeframe) {
    case 'Quarterly':
      return 'QoQ'
    case 'Annual':
      return 'YoY'
    default:
      return 'MoM'
  }
}

export const valueOptions = ({
  unit,
  format,
  kind,
  valueDecimals,
}: Figure): ValueOptions => ({
  unit,
  format,
  kind,
  valueDecimals,
})

/** Another value of the figure's card (e.g. the previous one), formatted like it. */
export const formatLike = (figure: Figure, value: number) =>
  formatValue(value, valueOptions(figure))
